import { createPortal } from 'react-dom';
import { CloseButton } from '../CloseButton';
import type { TourDefinition, TutorialsMenuLabels } from './types';
import './tour.css';

interface TutorialsMenuProps {
  tours: TourDefinition[];
  open: boolean;
  onSelect(tourId: string): void;
  onClose(): void;
  hasCompleted(tourId: string): boolean;
  /**
   * Opcional. Si devuelve true para un tourId, su tarjeta se muestra deshabilitada
   * (grisada, no clicable). El motor es agnóstico del porqué — el consumidor decide
   * (p.ej. un tour que solo aplica en cierta ruta). Por defecto ningún tour se deshabilita.
   */
  isDisabled?(tourId: string): boolean;
  /**
   * Opcional. Texto-ayuda mostrado bajo la tarjeta de un tour deshabilitado, explicando
   * qué hace falta para activarlo (ya resuelto por i18n en el consumidor). Si no se pasa,
   * la tarjeta se deshabilita sin texto adicional.
   */
  disabledHint?(tourId: string): string | undefined;
  labels?: TutorialsMenuLabels;
}

const DEFAULT_LABELS: Required<TutorialsMenuLabels> = {
  eyebrow: '🎓 Tutoriales',
  title: '¿Qué quieres aprender?',
  description: 'Elige un tutorial para empezar. Te llevará paso a paso por la pantalla real.',
  allCompleted: '🎉 ¡Has completado todos los tutoriales!',
  progress: (completed, total) => `${completed} de ${total} completados`,
  completedLabel: title => `${title} (completado)`,
};

/**
 * Menú lanzable con la lista de tutoriales disponibles.
 * Al pulsar un tutorial dispara onSelect(tourId). El chrome (cabecera, textos
 * fijos) llega ya resuelto vía `labels` — el motor es i18n-agnostic. Si no se
 * pasan labels, usa defaults en español. El contenido de cada tour (título/
 * descripción) también llega ya resuelto desde el consumidor.
 */
export function TutorialsMenu({
  tours,
  open,
  onSelect,
  onClose,
  hasCompleted,
  isDisabled,
  disabledHint,
  labels,
}: TutorialsMenuProps) {
  if (!open) return null;

  const eyebrow = labels?.eyebrow ?? DEFAULT_LABELS.eyebrow;
  const title = labels?.title ?? DEFAULT_LABELS.title;
  const description = labels?.description ?? DEFAULT_LABELS.description;
  const allCompletedText = labels?.allCompleted ?? DEFAULT_LABELS.allCompleted;
  const progressLabel = labels?.progress ?? DEFAULT_LABELS.progress;
  const completedLabel = labels?.completedLabel ?? DEFAULT_LABELS.completedLabel;

  // Los tours deshabilitados no son accionables → no cuentan en el progreso ni en el total.
  const activeTours = tours.filter(t => !(isDisabled?.(t.id) ?? false));
  const completedCount = activeTours.filter(t => hasCompleted(t.id)).length;
  const progress = activeTours.length > 0 ? (completedCount / activeTours.length) * 100 : 0;
  const allCompleted = activeTours.length > 0 && completedCount === activeTours.length;

  return createPortal(
    <div className="klonup-tutorials-menu-root">
      <div className="klonup-tutorials-menu-backdrop" onClick={onClose} />
      <div
        className="klonup-tutorials-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorials-menu-title"
      >
        {/* Header */}
        <div className="klonup-tutorials-menu-header">
          <div>
            <p className="klonup-tutorials-menu-eyebrow">{eyebrow}</p>
            <h2 id="tutorials-menu-title" className="klonup-tutorials-menu-title">
              {title}
            </h2>
          </div>
          <div className="klonup-tutorials-menu-close">
            <CloseButton onClick={onClose} ariaLabel="Cerrar" size="sm" />
          </div>
        </div>

        <p className="klonup-tutorials-menu-description">{description}</p>

        {/* Resumen de progreso */}
        <div className="klonup-tutorials-menu-progress">
          <span className="klonup-tutorials-menu-progress-text">
            {allCompleted ? allCompletedText : progressLabel(completedCount, activeTours.length)}
          </span>
          <div className="klonup-tutorials-menu-progress-bar">
            <div
              className="klonup-tutorials-menu-progress-fill"
              data-testid="progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Lista de tutoriales */}
        <div className="klonup-tutorials-menu-list">
          {tours.map(tour => {
            const disabled = isDisabled?.(tour.id) ?? false;
            const hint = disabled ? disabledHint?.(tour.id) : undefined;
            // Una tarjeta deshabilitada no se muestra como completada (no es accionable).
            const completed = !disabled && hasCompleted(tour.id);
            return (
              <button
                key={tour.id}
                type="button"
                className={`klonup-tutorial-card${completed ? ' is-completed' : ''}${disabled ? ' is-disabled' : ''}`}
                data-completed={completed ? 'true' : undefined}
                data-disabled={disabled ? 'true' : undefined}
                disabled={disabled}
                aria-disabled={disabled || undefined}
                onClick={disabled ? undefined : () => onSelect(tour.id)}
                aria-label={completed ? completedLabel(tour.title) : tour.title}
              >
                <div className="klonup-tutorial-card-icon">{tour.icon}</div>
                <div className="klonup-tutorial-card-body">
                  <p className="klonup-tutorial-card-title">{tour.title}</p>
                  <p className="klonup-tutorial-card-description">{tour.description}</p>
                  {hint && <p className="klonup-tutorial-card-hint">{hint}</p>}
                </div>
                {disabled ? (
                  <div className="klonup-tutorial-card-lock" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                ) : completed ? (
                  <div className="klonup-tutorial-card-check" aria-hidden="true">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                ) : (
                  <div className="klonup-tutorial-card-chevron">›</div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
